import React, { useState } from 'react';
import { 
    Clock, Star, TrendingUp, AlertTriangle, UserCheck, 
    ClipboardList, Calendar, Sparkles, ChevronRight, Save, BarChart3,
    Target, ShieldCheck, Key, Briefcase, History, FileText, X, Lock, Eye
} from 'lucide-react';
import { STAFF_METRICS, STAFF_LOGS } from '../data/mockData';

// Enhanced Mock Data
const ENHANCED_STAFF_METRICS = STAFF_METRICS.map(staff => ({
  ...staff,
  csat: staff.name === 'Sarah Sales' ? 4.9 : staff.name === 'Mike Compliance' ? 4.7 : 4.2, 
  aiFlagRate: staff.name === 'Sarah Sales' ? 1.2 : staff.name === 'Mike Compliance' ? 0.5 : 8.5, 
  reopenRate: staff.name === 'Sarah Sales' ? 3 : staff.name === 'Mike Compliance' ? 1 : 12, 
  status: staff.score > 90 ? 'Top Performer' : staff.score > 80 ? 'Good' : 'Under Review'
}));

// Local Mock History Data
const MOCK_REVIEW_HISTORY = [
    { id: 101, staff: 'Sarah Sales', date: '2025-11-01', period: 'Monthly', rating: 9, manager: 'Dave Manager', signed: true },
    { id: 102, staff: 'Sarah Sales', date: '2025-10-01', period: 'Quarterly', rating: 8, manager: 'Dave Manager', signed: true },
    { id: 103, staff: 'Mike Compliance', date: '2025-11-01', period: 'Monthly', rating: 7, manager: 'Dave Manager', signed: true },
];

const StaffPage = () => {
  const [activeTab, setActiveTab] = useState('kpi'); // 'kpi' | 'reviews'
  const [reviewState, setReviewState] = useState({ 
      step: 'select', // 'select' | 'form' | 'history' | 'view-archive'
      staff: null, 
      period: 'Monthly',
      archivedReview: null 
  });

  // Form State
  const [formData, setFormData] = useState({
      date: new Date().toISOString().split('T')[0],
      rating: 0,
      managerComments: '',
      growthProspects: '',
      trainingNeeds: '',
      payReview: '',
      smartS: '', smartM: '', smartA: '', smartR: '', smartT: '',
      staffComments: '',
      managerUser: '', managerPass: '', staffUser: '', staffPass: ''
  });

  const startReview = (staff) => {
      setReviewState({ ...reviewState, step: 'form', staff });
      setFormData({
          date: new Date().toISOString().split('T')[0],
          rating: 0,
          managerComments: '',
          growthProspects: '',
          trainingNeeds: '',
          payReview: '',
          smartS: '', smartM: '', smartA: '', smartR: '', smartT: '',
          staffComments: '',
          managerUser: '', managerPass: '', staffUser: '', staffPass: ''
      });
  };

  const viewHistory = (staff) => {
      setReviewState({ ...reviewState, step: 'history', staff });
  };

  const openArchivedReview = (review) => {
      setReviewState({ ...reviewState, step: 'view-archive', archivedReview: review });
  };

  const getAiReport = (staff, period) => {
      const timeframe = period === 'Monthly' ? 'last month' : period === 'Quarterly' ? 'last quarter' : 'past year';
      const multiplier = period === 'Monthly' ? 1 : period === 'Quarterly' ? 3.2 : 12.5;
      
      return {
          summary: `${staff.name} has demonstrated ${staff.score > 90 ? 'exceptional' : 'consistent'} adherence to compliance protocols over the ${timeframe}. Efficiency metrics have ${staff.trend === 'up' ? 'increased by 12%' : 'remained stable'} compared to the previous period. Agentic AI detected ${Math.floor(staff.aiFlagRate * multiplier)} minor anomalies which were self-corrected.`,
          strengths: ['High CSAT Score', 'Low AI Rejection Rate', 'Quick Ticket Resolution'],
          weaknesses: ['Occasional delay in email follow-ups', 'Needs refresher on new AML module'],
          metrics: {
              tickets: Math.floor(staff.tickets * multiplier),
              kyc: Math.floor(staff.kyc * multiplier),
              csat: staff.csat
          }
      };
  };

  // --- RENDERERS ---

  const renderReviewHistory = () => {
      const history = MOCK_REVIEW_HISTORY.filter(r => r.staff === reviewState.staff.name);
      
      return (
          <div className="animate-fade-in">
              <button 
                onClick={() => setReviewState({...reviewState, step: 'select', staff: null})} 
                className="text-slate-400 hover:text-white mb-6 flex items-center gap-2 text-sm font-bold"
              >
                  &larr; Back to Staff List
              </button>
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <History size={24} className="text-blue-500"/> Review History: {reviewState.staff.name}
              </h3>

              <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                  <table className="w-full text-left">
                      <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                          <tr>
                              <th className="px-6 py-4">Review Date</th>
                              <th className="px-6 py-4">Period Type</th>
                              <th className="px-6 py-4">Rating</th>
                              <th className="px-6 py-4">Manager</th>
                              <th className="px-6 py-4">Status</th>
                              <th className="px-6 py-4 text-right">Action</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-sm">
                          {history.map((review) => (
                              <tr key={review.id} className="hover:bg-slate-800/50 transition-colors">
                                  <td className="px-6 py-4 text-white font-mono">{review.date}</td>
                                  <td className="px-6 py-4 text-slate-300">
                                      <span className={`px-2 py-1 rounded text-xs font-bold uppercase border ${review.period === 'Annual' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                                          {review.period}
                                      </span>
                                  </td>
                                  <td className="px-6 py-4">
                                      <div className="flex items-center gap-1">
                                          <span className="font-bold text-white">{review.rating}/10</span>
                                          <Star size={12} className="text-amber-500 fill-amber-500"/>
                                      </div>
                                  </td>
                                  <td className="px-6 py-4 text-slate-400">{review.manager}</td>
                                  <td className="px-6 py-4">
                                      {review.signed ? (
                                          <span className="flex items-center gap-1 text-green-400 text-xs font-bold">
                                              <ShieldCheck size={14}/> Signed & Sealed
                                          </span>
                                      ) : (
                                          <span className="text-amber-500 text-xs">Pending Signature</span>
                                      )}
                                  </td>
                                  <td className="px-6 py-4 text-right">
                                      <button 
                                          onClick={() => openArchivedReview(review)}
                                          className="text-blue-400 font-bold text-xs hover:text-white transition-colors"
                                      >
                                          View Document
                                      </button>
                                  </td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
                  {history.length === 0 && (
                      <div className="p-12 text-center text-slate-500 italic">No past reviews found for this staff member.</div>
                  )}
              </div>
          </div>
      );
  };

  const renderReadOnlyReview = () => {
      const review = reviewState.archivedReview;
      return (
          <div className="animate-fade-in max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                  <button 
                    onClick={() => setReviewState({...reviewState, step: 'history'})} 
                    className="text-slate-400 hover:text-white flex items-center gap-2 text-sm font-bold"
                  >
                      &larr; Back to History
                  </button>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-900/30 border border-green-500/30 rounded-lg text-green-400 text-xs font-bold uppercase">
                      <Lock size={12} /> Archived Record • Read Only
                  </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
                  {/* Document Header */}
                  <div className="bg-slate-950 p-8 border-b border-slate-800 text-center">
                      <h2 className="text-2xl font-bold text-white mb-2">Performance Review: {reviewState.staff.name}</h2>
                      <p className="text-slate-400 text-sm">Period: {review.period} • Date: {review.date}</p>
                  </div>

                  <div className="p-8 space-y-8">
                      {/* Score Display */}
                      <div className="flex justify-center">
                          <div className="bg-slate-800 px-6 py-4 rounded-xl border border-slate-700 text-center">
                              <span className="text-xs text-slate-500 uppercase font-bold block mb-1">Overall Rating</span>
                              <span className="text-4xl font-bold text-white">{review.rating}<span className="text-lg text-slate-500">/10</span></span>
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-8">
                          <div>
                              <h4 className="text-sm font-bold text-slate-400 uppercase mb-3">Manager Comments</h4>
                              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 text-slate-300 text-sm italic min-h-[100px]">
                                  "Sarah has shown excellent initiative this month. Her handling of the complex AML case #992 was exemplary."
                              </div>
                          </div>
                          <div>
                              <h4 className="text-sm font-bold text-slate-400 uppercase mb-3">Staff Feedback</h4>
                              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 text-slate-300 text-sm italic min-h-[100px]">
                                  "Thank you. I appreciate the support on the new crypto module training."
                              </div>
                          </div>
                      </div>

                      {/* Signatures Section */}
                      <div className="border-t border-slate-800 pt-8 mt-8">
                          <h4 className="text-xs font-bold text-slate-500 uppercase mb-6 flex items-center gap-2">
                              <ShieldCheck size={14}/> Digital Signatures Verification
                          </h4>
                          <div className="flex justify-between items-center bg-green-900/10 border border-green-500/20 p-6 rounded-xl">
                              <div>
                                  <p className="text-xs text-green-400 font-bold uppercase mb-1">Signed by Manager</p>
                                  <p className="text-white font-mono text-sm">Dave Manager</p>
                                  <p className="text-[10px] text-slate-500 mt-1">Hash: 0x8a7...992b</p>
                              </div>
                              <div className="h-8 w-px bg-slate-700"></div>
                              <div className="text-right">
                                  <p className="text-xs text-green-400 font-bold uppercase mb-1">Signed by Staff</p>
                                  <p className="text-white font-mono text-sm">{reviewState.staff.name}</p>
                                  <p className="text-[10px] text-slate-500 mt-1">Hash: 0x3c1...11a4</p>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      );
  };

  const renderReviewForm = () => {
      const report = getAiReport(reviewState.staff, reviewState.period);
      const isExtended = reviewState.period === 'Quarterly' || reviewState.period === 'Annual';
      
      return (
          <div className="animate-fade-in pb-12">
              <button 
                onClick={() => setReviewState({...reviewState, step: 'select', staff: null})} 
                className="text-slate-400 hover:text-white mb-6 flex items-center gap-2 text-sm font-bold"
              >
                  &larr; Back to Staff List
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* LEFT: AI & DATA CONTEXT */}
                  <div className="space-y-6">
                      <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl">
                          <h3 className="text-xl font-bold text-white mb-1">{reviewState.staff.name}</h3>
                          <p className="text-slate-400 text-sm mb-6">{reviewState.staff.role} • {reviewState.period} Performance Review</p>
                          
                          <div className="grid grid-cols-3 gap-4 mb-6">
                              <div className="bg-slate-950 p-3 rounded-xl text-center">
                                  <span className="text-[10px] text-slate-500 uppercase font-bold">Tickets</span>
                                  <p className="text-xl font-bold text-white mt-1">{report.metrics.tickets}</p>
                              </div>
                              <div className="bg-slate-950 p-3 rounded-xl text-center">
                                  <span className="text-[10px] text-slate-500 uppercase font-bold">KYC Checks</span>
                                  <p className="text-xl font-bold text-blue-400 mt-1">{report.metrics.kyc}</p>
                              </div>
                              <div className="bg-slate-950 p-3 rounded-xl text-center">
                                  <span className="text-[10px] text-slate-500 uppercase font-bold">Avg CSAT</span>
                                  <p className="text-xl font-bold text-amber-500 mt-1">{report.metrics.csat}</p>
                              </div>
                          </div>

                          <div className="bg-indigo-900/10 border border-indigo-500/30 p-4 rounded-xl">
                              <h4 className="text-indigo-400 font-bold text-sm mb-3 flex items-center gap-2">
                                  <Sparkles size={16} /> Agentic AI Performance Analysis
                              </h4>
                              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                  {report.summary}
                              </p>
                              <div className="space-y-2">
                                  <div className="flex gap-2 text-xs">
                                      <span className="font-bold text-green-400">Strengths:</span>
                                      <span className="text-slate-400">{report.strengths.join(', ')}</span>
                                  </div>
                                  <div className="flex gap-2 text-xs">
                                      <span className="font-bold text-red-400">Areas for Growth:</span>
                                      <span className="text-slate-400">{report.weaknesses.join(', ')}</span>
                                  </div>
                              </div>
                          </div>
                      </div>

                      {/* Staff Feedback Area */}
                      <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl">
                          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                              <UserCheck size={20} className="text-blue-500" /> Staff Feedback
                          </h3>
                          <p className="text-xs text-slate-500 mb-2">To be completed by the staff member.</p>
                          <textarea 
                              className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-sm text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 outline-none h-32"
                              placeholder="Staff member: Please write your comments, rebuttals, or general feedback here..."
                              value={formData.staffComments}
                              onChange={(e) => setFormData({...formData, staffComments: e.target.value})}
                          ></textarea>
                      </div>
                  </div>

                  {/* RIGHT: MANAGER ASSESSMENT FORM */}
                  <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl flex flex-col h-full">
                      <div className="flex justify-between items-center mb-6">
                          <h3 className="font-bold text-white flex items-center gap-2">
                              <ClipboardList size={20} className="text-slate-400" /> Assessment
                          </h3>
                          <div className="flex items-center gap-2 bg-slate-950 px-3 py-1.5 rounded-lg border border-slate-700">
                              <Calendar size={14} className="text-slate-400"/>
                              <input 
                                  type="date" 
                                  className="bg-transparent text-xs text-white outline-none font-bold uppercase"
                                  value={formData.date}
                                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                              />
                          </div>
                      </div>
                      
                      <div className="space-y-6 flex-1 overflow-y-auto pr-2">
                          {/* Rating */}
                          <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Overall Performance Rating</label>
                              <div className="flex gap-2">
                                  {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                      <button 
                                        key={num} 
                                        onClick={() => setFormData({...formData, rating: num})}
                                        className={`flex-1 h-10 rounded-lg font-bold text-sm transition-colors ${formData.rating === num ? (num > 7 ? 'bg-green-600 text-white' : num > 4 ? 'bg-amber-500 text-slate-900' : 'bg-red-500 text-white') : 'bg-slate-800 text-slate-500 hover:bg-slate-700'}`}
                                      >
                                          {num}
                                      </button>
                                  ))}
                              </div>
                          </div>

                          {/* Basic Comments */}
                          <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Manager's Evaluation</label>
                              <textarea 
                                  className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-sm text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 outline-none h-32"
                                  placeholder="Provide detailed feedback on performance..."
                                  value={formData.managerComments}
                                  onChange={(e) => setFormData({...formData, managerComments: e.target.value})}
                              ></textarea>
                          </div>

                          {/* Extended Fields (Quarterly/Annual) */}
                          {isExtended && (
                              <div className="p-4 bg-slate-950/50 rounded-xl border border-slate-800 space-y-4">
                                  <h4 className="text-sm font-bold text-white flex items-center gap-2 mb-2"><Briefcase size={16} className="text-amber-500"/> Deep Dive Review</h4>
                                  
                                  <div>
                                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Growth Prospects & Career Path</label>
                                      <textarea className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white h-20" placeholder="Discuss promotion readiness and long-term fit..." value={formData.growthProspects} onChange={(e) => setFormData({...formData, growthProspects: e.target.value})}></textarea>
                                  </div>
                                  <div>
                                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Training Needs & Skill Gaps</label>
                                      <textarea className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white h-20" placeholder="Identify areas for upskilling..." value={formData.trainingNeeds} onChange={(e) => setFormData({...formData, trainingNeeds: e.target.value})}></textarea>
                                  </div>
                                  <div>
                                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Compensation & Pay Review</label>
                                      <textarea className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white h-20" placeholder="Salary adjustment recommendations..." value={formData.payReview} onChange={(e) => setFormData({...formData, payReview: e.target.value})}></textarea>
                                  </div>
                              </div>
                          )}

                          {/* SMART Goals */}
                          <div className="p-4 bg-blue-900/10 border border-blue-500/30 rounded-xl space-y-4">
                               <h4 className="text-sm font-bold text-blue-400 flex items-center gap-2"><Target size={16} /> S.M.A.R.T Goal Setting</h4>
                               <div className="space-y-3">
                                   <div>
                                       <label className="block text-[10px] font-bold text-blue-300/70 uppercase mb-1">Specific (What needs to be accomplished?)</label>
                                       <input className="w-full bg-slate-900 border border-blue-500/20 rounded-lg p-2 text-sm text-white" value={formData.smartS} onChange={(e) => setFormData({...formData, smartS: e.target.value})} />
                                   </div>
                                   <div className="grid grid-cols-2 gap-3">
                                       <div>
                                            <label className="block text-[10px] font-bold text-blue-300/70 uppercase mb-1">Measurable</label>
                                            <input className="w-full bg-slate-900 border border-blue-500/20 rounded-lg p-2 text-sm text-white" placeholder="Metrics?" value={formData.smartM} onChange={(e) => setFormData({...formData, smartM: e.target.value})} />
                                       </div>
                                       <div>
                                            <label className="block text-[10px] font-bold text-blue-300/70 uppercase mb-1">Achievable</label>
                                            <input className="w-full bg-slate-900 border border-blue-500/20 rounded-lg p-2 text-sm text-white" placeholder="Realistic?" value={formData.smartA} onChange={(e) => setFormData({...formData, smartA: e.target.value})} />
                                       </div>
                                   </div>
                                   <div className="grid grid-cols-2 gap-3">
                                       <div>
                                            <label className="block text-[10px] font-bold text-blue-300/70 uppercase mb-1">Relevant</label>
                                            <input className="w-full bg-slate-900 border border-blue-500/20 rounded-lg p-2 text-sm text-white" placeholder="Why now?" value={formData.smartR} onChange={(e) => setFormData({...formData, smartR: e.target.value})} />
                                       </div>
                                       <div>
                                            <label className="block text-[10px] font-bold text-blue-300/70 uppercase mb-1">Time-Bound</label>
                                            <input className="w-full bg-slate-900 border border-blue-500/20 rounded-lg p-2 text-sm text-white" placeholder="Deadline?" value={formData.smartT} onChange={(e) => setFormData({...formData, smartT: e.target.value})} />
                                       </div>
                                   </div>
                               </div>
                          </div>

                          {/* Digital Signature */}
                          <div className="border-t border-slate-800 pt-6 mt-6">
                              <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><ShieldCheck size={16} className="text-green-500"/> Digital Signature & Finalization</h4>
                              <div className="grid grid-cols-2 gap-4">
                                  <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
                                      <p className="text-xs text-slate-500 uppercase font-bold mb-2">Manager Sign-off</p>
                                      <input className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-xs text-white mb-2" placeholder="Username" value={formData.managerUser} onChange={(e) => setFormData({...formData, managerUser: e.target.value})} />
                                      <input className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-xs text-white" type="password" placeholder="Password" value={formData.managerPass} onChange={(e) => setFormData({...formData, managerPass: e.target.value})} />
                                  </div>
                                  <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
                                      <p className="text-xs text-slate-500 uppercase font-bold mb-2">Staff Sign-off</p>
                                      <input className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-xs text-white mb-2" placeholder="Username" value={formData.staffUser} onChange={(e) => setFormData({...formData, staffUser: e.target.value})} />
                                      <input className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-xs text-white" type="password" placeholder="Password" value={formData.staffPass} onChange={(e) => setFormData({...formData, staffPass: e.target.value})} />
                                  </div>
                              </div>
                              <button 
                                onClick={() => { 
                                    if(!formData.rating) return alert('Please provide a rating.');
                                    if(!formData.managerPass || !formData.staffPass) return alert('Both parties must sign to finalize.');
                                    alert(`Review Finalized for ${reviewState.staff.name}. Saved to blockchain audit trail.`); 
                                    setReviewState({...reviewState, step: 'select', staff: null}); 
                                }}
                                className="w-full mt-4 bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-green-900/20"
                              >
                                  <Key size={18} /> Finalize & Submit Review
                              </button>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      );
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Staff Management</h2>
            <p className="text-slate-400 mt-1">Monitor performance, quality assurance, and conduct reviews.</p>
        </div>
        
        {/* TAB SWITCHER */}
        <div className="bg-slate-900 p-1 rounded-lg border border-slate-800 flex">
            <button 
                onClick={() => setActiveTab('kpi')}
                className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'kpi' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
            >
                <BarChart3 size={14} /> KPI Dashboard
            </button>
            <button 
                onClick={() => setActiveTab('reviews')}
                className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'reviews' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
            >
                <ClipboardList size={14} /> Performance Reviews
            </button>
        </div>
      </div>

      {activeTab === 'kpi' && (
        <>
            {/* KPI METRICS (Existing) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {ENHANCED_STAFF_METRICS.map((staff, i) => (
                    <div key={i} className="bg-slate-900 p-6 rounded-2xl border border-slate-800 relative overflow-hidden group hover:border-slate-700 transition-colors">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold border border-slate-600">
                                    {staff.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <div>
                                    <h3 className="font-bold text-white text-lg leading-tight">{staff.name}</h3>
                                    <span className="text-xs text-slate-500 uppercase font-bold">{staff.role}</span>
                                </div>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-bold border ${
                                staff.status === 'Top Performer' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 
                                staff.status === 'Under Review' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 
                                'bg-blue-500/10 text-blue-400 border-blue-500/20'
                            }`}>
                                {staff.status}
                            </div>
                        </div>

                        {/* Efficiency Section */}
                        <div className="mb-6 p-4 bg-slate-950/50 rounded-xl border border-slate-800/50">
                            <h4 className="text-[10px] text-slate-500 uppercase font-bold mb-3 flex items-center gap-2"><TrendingUp size={12}/> Efficiency Metrics</h4>
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div><div className="text-lg font-mono font-bold text-white">{staff.kyc}</div><div className="text-[10px] text-slate-500">KYC Checks</div></div>
                                <div><div className="text-lg font-mono font-bold text-white">{staff.tickets}</div><div className="text-[10px] text-slate-500">Tickets</div></div>
                                <div><div className="text-lg font-mono font-bold text-blue-400">{staff.time}</div><div className="text-[10px] text-slate-500">Avg Speed</div></div>
                            </div>
                        </div>

                        {/* Quality Section */}
                        <div className="space-y-4">
                            <h4 className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-2"><UserCheck size={12}/> Quality Assurance</h4>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-400">Customer Satisfaction</span>
                                <div className="flex items-center gap-1"><span className="text-white font-bold">{staff.csat}</span><div className="flex">{[...Array(5)].map((_, i) => (<Star key={i} size={10} className={i < Math.round(staff.csat) ? "text-amber-500 fill-amber-500" : "text-slate-700"} />))}</div></div>
                            </div>
                            <div>
                                <div className="flex justify-between items-center mb-1"><span className="text-sm text-slate-400 flex items-center gap-1">AI Flag Rate {staff.aiFlagRate > 5 && <AlertTriangle size={12} className="text-red-500"/>}</span><span className={`text-sm font-bold ${staff.aiFlagRate > 5 ? 'text-red-500' : 'text-green-500'}`}>{staff.aiFlagRate}%</span></div>
                                <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"><div className={`h-full rounded-full ${staff.aiFlagRate > 5 ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${staff.aiFlagRate * 5}%` }}></div></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Live Feed */}
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Clock size={18} /> Live Quality Audit Log</h3>
                <div className="space-y-4">{STAFF_LOGS.map(log => (<div key={log.id} className="flex items-center gap-4 p-3 bg-slate-950/50 rounded-lg border border-slate-800 hover:bg-slate-900 transition-colors"><div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-400 border border-slate-700">{log.staff.split(' ')[0][0]}</div><div className="flex-1"><p className="text-sm text-white"><span className="font-bold text-blue-400">{log.staff}</span> <span className="text-slate-400"> performed </span><span className="font-medium text-slate-200">{log.action}</span></p><p className="text-xs text-slate-500 mt-0.5">Target: {log.target}</p></div><div className="text-right"><span className="text-xs text-slate-500 font-mono block">{log.time}</span><span className="text-[10px] bg-green-500/10 text-green-500 px-1.5 py-0.5 rounded border border-green-500/20">Verified</span></div></div>))}</div>
            </div>
        </>
      )}

      {activeTab === 'reviews' && (
          <div className="space-y-6">
              {reviewState.step === 'select' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Period Selector Header */}
                      <div className="col-span-full flex justify-end mb-4">
                          <div className="bg-slate-900 p-1 rounded-lg border border-slate-800 flex">
                              {['Monthly', 'Quarterly', 'Annual'].map(period => (
                                  <button 
                                    key={period} 
                                    onClick={() => setReviewState({...reviewState, period})}
                                    className={`px-4 py-1.5 rounded text-xs font-bold transition-all ${reviewState.period === period ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                  >
                                      {period}
                                  </button>
                              ))}
                          </div>
                      </div>

                      {/* Staff Cards for Selection */}
                      {ENHANCED_STAFF_METRICS.map((staff, i) => (
                          <div key={i} className="bg-slate-900 p-6 rounded-2xl border border-slate-800 hover:border-blue-500/50 transition-all cursor-pointer group" onClick={() => startReview(staff)}>
                              <div className="flex justify-between items-center mb-4">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold text-lg border border-slate-600">{staff.name.split(' ').map(n => n[0]).join('')}</div>
                                  <div className="text-right">
                                      <span className="text-xs text-slate-500 block">Last Review</span>
                                      <span className="text-sm font-bold text-white">28 Days Ago</span>
                                  </div>
                              </div>
                              <h3 className="text-xl font-bold text-white mb-1">{staff.name}</h3>
                              <p className="text-slate-500 text-sm mb-6">{staff.role}</p>
                              <div className="flex items-center justify-between mt-4">
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); viewHistory(staff); }}
                                    className="text-xs font-bold text-slate-400 hover:text-white flex items-center gap-1"
                                  >
                                      <History size={14}/> History
                                  </button>
                                  <div className="flex items-center gap-2 text-xs font-bold text-blue-400 bg-blue-900/10 px-3 py-1.5 rounded-lg border border-blue-500/20 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                                      <span>Start {reviewState.period}</span>
                                      <ChevronRight size={14} />
                                  </div>
                              </div>
                          </div>
                      ))}
                  </div>
              ) : reviewState.step === 'history' ? (
                  renderReviewHistory()
              ) : reviewState.step === 'view-archive' ? (
                  renderReadOnlyReview()
              ) : (
                  renderReviewForm()
              )}
          </div>
      )}
    </div>
  );
};

export default StaffPage;